if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;

        const variantRadios = document.querySelector('variant-radios');
        const sizeName = variantRadios?.dataset.sizeName;

        this.sizeInputs = document.querySelectorAll(`[name="${sizeName}"]`);

        this.recipientFields = document.querySelectorAll('.recipient-fields');

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (this.recipientFields) {
          Array.from(this.recipientFields).forEach((recipientField) => {
            const input =
              recipientField.querySelector('input') || recipientField.querySelector('textarea');
            input.addEventListener('focus', () => {
              recipientField.classList.remove('error');
            });
          });
        }

        if (this.sizeInputs) {
          Array.from(this.sizeInputs).forEach((input) => {
            const isChecked = input.checked;
            const isAvailable = this.variantIsAvailable(input);

            if (isChecked) {
              this.updateScarcityMessage(input);

              if (!isAvailable) {
                this.onUnavailableVariantSelected();
              }
            }

            input.addEventListener('change', this.onVariantSelect.bind(this));
          });
        }

        this.initDeviceBasedAnimations();

        window.addEventListener('resize', () => {
          this.handleScreenResize();
        });
      }

      initDeviceBasedAnimations() {
        this.isDesktop = window.innerWidth >= 768;
        if (this.isDesktop) {
          this.startVariantAnimations();
        } else {
          this.initVariantAnimationObserver();
        }
      }

      initVariantAnimationObserver() {
        const variantWrapper = document.querySelector('.product-form-variant-wrapper');

        if (!variantWrapper) {
          const alternativeWrapper =
            document.querySelector('variant-radios') ||
            document.querySelector('.product-form__input');
          if (alternativeWrapper) {
            this.observeElement(alternativeWrapper);
          }
          return;
        }

        this.observeElement(variantWrapper);
      }

      observeElement(element) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setTimeout(() => {
                  this.startVariantAnimations();
                }, 500);

                observer.disconnect();
              }
            });
          },
          {
            threshold: 0.1,
            rootMargin: '0px',
          }
        );

        observer.observe(element);
      }

      startVariantAnimations() {
        const unavailableLabels = document.querySelectorAll('.product-form__label.unavailable');

        unavailableLabels.forEach((label) => {
          label.classList.add('animate-variant');
        });
      }

      onUnavailableVariantSelected() {
        const event = new CustomEvent(window.FarmRio.events.selectedUnavailableVariant);
        document.dispatchEvent(event);
      }

      variantIsAvailable(input) {
        const availableQuantity = parseInt(input.dataset.availablequantity);
        return availableQuantity > 0;
      }

      onVariantSelect(event) {
        const isAvailable = this.variantIsAvailable(event.target);

        this.updateScarcityMessage(event.target);

        if (!isAvailable) {
          this.onUnavailableVariantSelected();
        }

        this.updateAddToCartButton(isAvailable);

        if (window.FarmRio.events.selectedProductVariant) {
          const selectedProductVariantEvent = new CustomEvent(
            window.FarmRio.events.selectedProductVariant,
            {
              detail: {
                variantName: event.target.value,
              },
            }
          );

          document.dispatchEvent(selectedProductVariantEvent);
        }
      }

      updateScarcityMessage(input) {
        const scarcityMessage = document.getElementById('product-scarcity-message');

        if (!scarcityMessage) {
          return;
        }

        const messageElement = scarcityMessage.querySelector('p');

        if (!messageElement) {
          return;
        }

        const quantity = parseInt(input.dataset.availablequantity, 10);

        const variantRadios = input.closest('variant-radios');

        if (!variantRadios) {
          return;
        }

        const messageOne = variantRadios.dataset.scarcityMessageOne;
        const messageOther = variantRadios.dataset.scarcityMessageOther;

        if (quantity >= 1 && quantity <= 5) {
          messageElement.textContent = messageOther;
          scarcityMessage.classList.remove('tw-hidden');
        } else if (quantity >= 6 && quantity <= 10) {
          messageElement.textContent = messageOne;
          scarcityMessage.classList.remove('tw-hidden');
        } else {
          messageElement.textContent = '';
          scarcityMessage.classList.add('tw-hidden');
        }
      }

      updateAddToCartButton(isAvailable) {
        const buttonText = document.getElementById('button-text');
        const notifyBell = document.getElementById('notify-bell');
        const addToCartButton = document.getElementById('add-to-bag');

        if (!buttonText || !notifyBell || !addToCartButton) {
          return;
        }

        if (isAvailable) {
          buttonText.textContent = buttonText.getAttribute('data-add-to-cart-text') || 'Add to bag';
          notifyBell.classList.add('tw-hidden');
          addToCartButton.classList.remove('notify-me-pdp-btn');
        } else {
          buttonText.textContent = 'Notify Me When Available';
          notifyBell.classList.remove('tw-hidden');
          addToCartButton.classList.add('notify-me-pdp-btn');
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        const submitButton = this.querySelector('[type="submit"]');
        if (submitButton.classList.contains('loading')) {
          return;
        }

        if (submitButton.classList.contains('notify-me-pdp-btn')) {
          const variantId = submitButton.dataset.variantId;
          const backInStock = document.querySelector('back-in-stock');

          backInStock.setVariantId(+variantId);
          backInStock.openModal(evt);

          return;
        }

        if (evt.target.closest('.is-float')) {
          document.dispatchEvent(
            new Event(window.FarmRio.events.addProductToCartFromFloatInformation)
          );
        }

        this.handleErrorMessage();
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');
        const isNewCart = !window.getCookie('cart');

        const formData = new FormData(this.form);
        const variant_id = formData.get('id');

        const properties = {};

        function extractKey(str) {
          const match = str.match(/\[(.*?)\]/);
          return match ? match[1] : null;
        }

        for (const [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const _key = extractKey(key);
            properties[_key] = value;
          }
        }
        const productId = formData.get('product_id');
        const watched_product_video = sessionStorage.getItem('watched_product_video');
        if (watched_product_video && productId) {
          const productsIds = JSON.parse(watched_product_video);
          const userWatchedVideo = productsIds.find((id) => String(id) === String(productId));
          if (userWatchedVideo) {
            properties['_watched_product_video'] = 'true';
          }
        }

        await Cart.addProductVariantToCart(Number(variant_id), 1, properties);

        if (isNewCart && window.FarmRio.events.createCart) {
          document.dispatchEvent(new Event(window.FarmRio.events.createCart));
        }

        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading-overlay__spinner').classList.add('hidden');
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        const handleFieldErrorMessage = (message, field) => {
          if (!message || !field) {
            return;
          }
          const $wrapper = this.form.querySelector(`.recipient-fields__field.${field}`);
          if (!$wrapper) {
            return;
          }
          $wrapper.classList.add('error');
          const $errorWrapper = $wrapper.querySelector('.form__message');
          if (!$errorWrapper) {
            return;
          }
          $errorWrapper.classList.remove('hidden');
          const $errorMessageElement = $errorWrapper.querySelector('.error-message');
          if (!$errorMessageElement) {
            return;
          }
          $errorMessageElement.innerHTML = message;
        };

        if (typeof errorMessage === 'string') {
          this.errorMessage.textContent = errorMessage;
        }

        if (errorMessage && typeof errorMessage === 'object' && !Array.isArray(errorMessage)) {
          const fields = Object.keys(errorMessage);

          const generalError = fields.reduce((acc, key) => {
            const fieldErrorMessage = errorMessage[key].join('. ');
            const specificErrorFields = ['send_on', 'email'];
            if (specificErrorFields.includes(key)) {
              handleFieldErrorMessage(fieldErrorMessage, key);
              return acc;
            } else {
              return acc.concat(fieldErrorMessage);
            }
          }, '');

          if (generalError) {
            this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
            this.errorMessage.textContent = generalError;
          }
        }
      }
    }
  );

  // Envia os dados para a Nosto quando o produto for adicionado ao carrinho
  if (window.FarmRio.events.addToCart) {
    document.addEventListener(window.FarmRio.events.addToCart, (event) => {
      nostojs((api) => api.loadRecommendations());
    });
  }
}
